import { Communication, AttachmentPreview } from '../../../providers/communication/communication';
import { TidyStorage } from '../../../shared/providers/tidy-storage';
import { Component, ElementRef, OnInit, OnDestroy, ViewEncapsulation, ViewChild, AfterViewInit, Renderer2 } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { InfiniteScrollCustomEvent, ModalController } from '@ionic/angular';
import { interval, lastValueFrom, Subscription } from 'rxjs';
import { Schedule } from 'src/providers/schedule/schedule';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Util } from 'src/shared/util/util';
import { MessageInputComponent } from '../../../tidy-ui-components/components/form/message-input/message-input';
import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Pros } from 'src/providers/pros/pros';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { WindowService } from "src/shared/providers/window.service";
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

@Component({
    selector: 'message-thread',
    templateUrl: 'message-thread.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./message-thread.scss'],
    standalone: false
})

export class MessageThreadPage implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('messageContent', { static: false }) messageContent: any;
  @ViewChild('messageInput') messageInput: MessageInputComponent;
  isMobileResolution = false;
  messageForm: UntypedFormGroup;
  cameFromNotificationsPage: boolean;
  proList: any[];
  pros: any[];
  isSendingMessage = false;
  attachmentPreviews: AttachmentPreview[] = [];
  isProSelected = false;
  pro: any;
  messages: any[] = [];
  isMessagesLoaded = false;
  proId: number;
  chatRoomKey: string;
  guestId: number;
  pageTitle = 'Messages';
  pageSubtitle = '';
  // Drives the top-right header icon: view contact (person) when we know the
  // person, add contact (plus) when the number is unassigned.
  contactInfo: any = null;
  assignLabel = '';
  assignList = [];
  showAssign = false;
  showSendToField: boolean;
  showAssignField: boolean;
  private linkActionSubscription: Subscription;
  private dropDownMenuSubscription: Subscription;
  refreshTimerSubscription: Subscription;
  private totalMessages = 0;
  private destroyed = false;
  dialogParams: any;
  backPage: string;
  loaded = false;
  page = 1;
  perPage = 50;
  totalMessage: number;
  keyboardHeight = 0;



  activeFeedbackMessageId: number | null = null;
  closingFeedbackMessageId: number | null = null;
  activeFeedbackType: 'positive' | 'negative' = 'positive';
  isSubmittingFeedback = false;

  // First-load skeleton scaffolding: deterministic sender sides/grouping so
  // the placeholder thread reads like a real conversation; widths and
  // heights vary per nth-child in the page styles.
  readonly skeletonThreadBubbles: { side: 'other' | 'user'; last: boolean }[] = [
    { side: 'other', last: false },
    { side: 'other', last: true },
    { side: 'user', last: true },
    { side: 'other', last: true },
    { side: 'user', last: false },
    { side: 'user', last: true },
    { side: 'other', last: false },
    { side: 'other', last: true },
    { side: 'user', last: true },
    { side: 'other', last: true },
    { side: 'user', last: false },
    { side: 'user', last: true },
  ];

  boundAssignNumber = this.assignNumber.bind(this);
  boundSendMessage = this.sendMessage.bind(this);
  boundSendAttachment = this.sendAttachment.bind(this);

constructor(
    private modalCtrl: ModalController,
    private fb: FormBuilder,
    private storage: TidyStorage,
    private util: Util,
    private schedule: Schedule,
    private communication: Communication,
    private conciergeApi: ConciergeApiService,
    private el: ElementRef,
    private rightSidePanelService: RightSidePanelService,
    private navCtrl: CustomNavController,
    private prosProvider: Pros,
    public windowService: WindowService,
    private renderer: Renderer2,
    private demoBlock: DemoBlockService
  ) {
    this.messageForm = this.fb.group({
      pro: ['', Validators.required],
      message: [''],
      assignedTo: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.dialogParams = await this.storage.retrieve('dialog-params');
      this.rightSidePanelService.setDialogPageTitle('Messages');
      this.attachmentPreviews = [];
      this.isMobileResolution = !this.windowService.isDesktopRes;
      this.proList = await this.getParam('proList');
      this.pros = await this.getParam('pros') || await this.getPros();
      this.backPage = await this.getParam('backPage');
      this.proId = await this.getParam('proId');
      this.chatRoomKey = await this.getParam('chatRoomKey');
      this.guestId = await this.getParam('guestId');
      this.contactInfo = await this.getParam('contactInfo');
      // Only match on an id we actually have. With both operands undefined a
      // bare `pro?.chat_room_key === this.chatRoomKey` would match the first pro
      // that has no chat_room_key, i.e. an arbitrary pro (the "name changes" bug).
      this.pro = this.pros.find((pro) =>
        (this.proId !== null && this.proId !== undefined && pro?.id === this.proId) ||
        (this.chatRoomKey !== null && this.chatRoomKey !== undefined && pro?.chat_room_key === this.chatRoomKey)
      );
      this.showSendToField = true;
      if (this.proId || this.chatRoomKey) {
        this.showSendToField = false;
        this.messageForm.get('pro').setValue(this.proId || this.chatRoomKey);
        await this.onSelectPro(this.proId || this.chatRoomKey);
      } else if (this.guestId) {
        await this.setupGuestThread();
      }
      if (!this.contactInfo) {
        this.contactInfo = this.deriveContactInfoFromPro();
      }
      this.loaded = true;
      if (this.destroyed) {return;}
      if (!this.showAssign && this.windowService.isDesktopRes) {
        this.rightSidePanelService.currentComponentData = { dropDownMenu: messageThreadDropDownMenu };
        this.rightSidePanelService.setLinkActionData('assets/svg/call-white.svg');
      }
      this.linkActionSubscription = this.rightSidePanelService.linkAction$.subscribe(() => {
        this.callPro();
      });
      this.dropDownMenuSubscription = this.rightSidePanelService.dropDownMenuAction$.subscribe((action) => {
        if (action === 'view-profile') {
          const params = {
            proId: this.pro.id,
          }
          this.rightSidePanelService.navigateTo(`my-pro/${this.pro.id}`, params, MyProPage);
        }
      });
      this.refreshTimerSubscription = interval(10000).subscribe(() => {
        this.getNewMessages();
      });
    } catch (err) {
      this.util.showError(err.error ? err.error.message : err.message);
    }
  }

  async getParam(paramName): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName)
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    return param;
  }

  async getPros() {
    const pros = await this.prosProvider.getAllPros();
    this.storage.save('pros', pros);
    return pros
  }

  // Opens an empty thread for a guest that has no existing conversation yet.
  // The first message is sent with `to_guest_id` (see sendMessage), which the
  // backend uses to create the guest chat room and deliver via the guest smart number.
  private async setupGuestThread(): Promise<void> {
    this.showSendToField = false;
    this.pageTitle = await this.getParam('pageTitle') || 'Guest';
    this.pro = { isGuest: true, guestId: this.guestId, first_name: this.pageTitle };
    this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    this.messageForm.get('pro').setValue(String(this.guestId));
    this.messages = [];
    // The template only draws the thread once a recipient is selected; without
    // this, messages sent into the new guest thread never render.
    this.isProSelected = true;
    this.isMessagesLoaded = true;
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.linkActionSubscription) {
      this.linkActionSubscription.unsubscribe();
    }
    if (this.dropDownMenuSubscription) {
      this.dropDownMenuSubscription.unsubscribe();
    }
    if (this.refreshTimerSubscription) {
      this.refreshTimerSubscription.unsubscribe();
    }
    if (Capacitor.getPlatform() === 'ios') {
      Keyboard.removeAllListeners();
    }
    this.rightSidePanelService.setLinkActionData(null);
  }

  ngAfterViewInit() {
    if (!Capacitor.isNativePlatform()) {
      setTimeout(() => {
        this.focusMessageInput();
      }, 500);
    }
  }

  private focusMessageInput() {
    if (this.messageInput && !Capacitor.isNativePlatform()) {
      this.messageInput.focusInput();
    }
  }

  async sendAttachment(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    try {
      const attachment = await this.communication.addAttachment();
      if (!attachment?.url) {
        this.util.showError('Unable to attach file. Please try again.');
        return;
      }
      this.modalCtrl.getTop().then((res) => {
        if (res) {
          res.setCurrentBreakpoint(0.65);
        }
      });
      this.attachmentPreviews.push(attachment);
    } catch (err) {
      const errorMessage = err.error ? err.error.message : err.message;
      this.util.showError(errorMessage);
    }
  }

  removeAttachment(index: number): void {
    this.attachmentPreviews.splice(index, 1);
  }

  openFile(url: string): void {
    window.open(url, '_blank');
  }

  downloadFile = async (url: string): Promise<void> => {
    try {
      const response = await fetch(url);
      const contentType = (response.headers.get('content-type') || '').split(';')[0].trim();
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const pathname = new URL(url).pathname;
      let filename = decodeURIComponent(pathname.split('/').pop() || 'download');
      if (!filename.includes('.')) {
        const ext = this.getExtensionFromMimeType(contentType)
          || this.getExtensionFromMimeType(blob.type)
          || (this.looksLikeImageUrl(url) ? '.jpg' : '');
        if (ext) {
          filename += ext;
        }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      window.open(url, '_blank');
    }
  }

  private getExtensionFromMimeType(mime: string): string {
    if (!mime || mime === 'application/octet-stream') {return '';}
    if (mime.startsWith('image/')) {
      const sub = mime.split('/')[1];
      return sub === 'jpeg' ? '.jpg' : sub === 'svg+xml' ? '.svg' : `.${sub}`;
    }
    const map: Record<string, string> = {
      'application/pdf': '.pdf',
      'text/csv': '.csv',
      'text/plain': '.txt',
    };
    return map[mime] || '';
  }

  private looksLikeImageUrl(url: string): boolean {
    return url.includes('/Media/ME') || url.includes('message-thread-photo/');
  }

  async sendMessage(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    this.isSendingMessage = true;
    this.util.closeLastOpenedToast();
    if (this.messageForm.invalid) {
      this.util.showWarning('Please select a pro to continue.');
      return;
    }
    if (
      this.messageForm.get('message').value === '' &&
      this.attachmentPreviews.length === 0
    ) {
      this.util.showWarning('Please add a message or attachment to continue.');
      return;
    }
    const payload: any = {
      message: {
        text: this.messageForm.value.message,
        files: this.attachmentPreviews.map(a => a.url),
      },
    };
    const chatRoomKey = this.pro?.chat_room_key || this.chatRoomKey;
    if (chatRoomKey) {
      payload['to_chat_room_key'] = chatRoomKey;
    } else if (this.pro?.isGuest && this.guestId) {
      payload['to_guest_id'] = this.guestId;
      // Ask the backend to echo the (possibly freshly-created) guest room key so we
      // can adopt it for polling/read-receipts on subsequent sends.
      payload['includes'] = 'chat_room_key';
    } else if (this.proId || this.pro?.id) {
      payload['to_homekeeper_id'] = this.proId || this.pro?.id;
    } else {
      const selectedPro = this.proList.find((pro) => pro.value === this.messageForm.get('pro').value);
      payload['to_homekeeper_id'] = selectedPro.proId;
    }
    try {
      const response = await this.communication.sendSharedInboxMessage(payload);
      if (response?.data?.message ==="We couldn't send your message, please try again later!") {
        const errorMessage = `Error sending message. Please try again later. Error message: ${  response.data.message}`;
        this.util.showError(errorMessage);
        return;
      }
      // Once a guest thread is created server-side, adopt its chat room key so
      // polling/read-receipts target the real room on subsequent sends.
      if (this.pro?.isGuest && !this.pro.chat_room_key) {
        const newKey = response?.chat_room_key || response?.chat_room?.key;
        if (newKey) {
          this.pro.chat_room_key = newKey;
          this.chatRoomKey = newKey;
        }
      }
      this.messages.push(response);
      this.messageForm.get('message').reset();
      this.attachmentPreviews = [];
      this.communication.setUpdateMessages();
      // Add delay to ensure DOM is updated before scrolling
      setTimeout(() => {
        if (this.messageContent) {
          this.messageContent.scrollToBottom(300);
        }
        // Focus input after everything else is done
        this.focusMessageInput();
      }, 100);
    } catch (err) {
      const errorMessage = `Error sending message. Please try again later. Error message: ${  err.error ? err.error.message : err.message}`;
      this.util.showError(errorMessage);
    }
    this.isSendingMessage = false;
  }

  async onSelectPro(id: number | string): Promise<void> {
    this.isProSelected = true;
    this.pro = this.pros.find((pro) => pro.id === id || pro?.chat_room_key === id);
    if (!this.pro) {
      //This triggers for non private pro that client has never had before + delegated pros
      await this.getPro();
    }
    const passedTitle = await this.getParam('pageTitle');
    if (this.pro?.chat_room_key && passedTitle) {
      // Proxy conversation: the pro object from list-proxy-numbers has no name
      // (members are omitted), so use the display name computed by the list.
      this.pageTitle = passedTitle;
    } else {
      this.pageTitle = this.pro?.first_name + (this.pro?.last_name ? ` ${  this.pro?.last_name}` : '');
      this.pageTitle = this.pageTitle.length > 40 ? `${this.pageTitle.substring(0, 40)  }..` : this.pageTitle;
    }
    this.pageSubtitle = (this.contactInfo?.showCategorySuffix && this.contactInfo?.category)
      ? `(${this.contactInfo.category})`
      : '';
    this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    this.messageForm.get('assignedTo').reset();
    this.showAssign = false;
    await this.getInitialMessages();
    this.isMessagesLoaded = true;
    if (this.pro?.chat_room_key) {
      this.handleAssignInput();
    }
    setTimeout(() => {
      if (this.messageContent) {
        this.messageContent.scrollToBottom(0);
      }
    }, 200);
    this.markMessagesAsRead();
  }

  async getPro() {
    this.cameFromNotificationsPage = await this.getParam('cameFromNotificationsPage');
    if (this.cameFromNotificationsPage || this.chatRoomKey) {
      this.pro = {
        chat_room_key: this.chatRoomKey || await this.getParam('chatRoomKey'),
        first_name: await this.getParam('pageTitle') || this.pageTitle
      };
    } else {
      const pro = await this.getParam('proDetail') || await this.prosProvider.getProDetail(this.proId);
      this.pro = {
        first_name: pro.homekeeper.first_name,
        last_name: pro.homekeeper.last_name,
        id: this.proId,
        main_team_id: pro.team.id
      };
    }
  }

  async markMessagesAsRead() {
    const firstUnreadMessage = this.messages.find((message) =>
      !message.is_read && (message?.chat_room_key || this.pro?.chat_room_key)
    );
    if (firstUnreadMessage) {
      await this.communication.markMessagesAsRead([firstUnreadMessage?.chat_room_key || this.pro?.chat_room_key]);
    }
  }

  async getMessagesFromServer(page, perPage) {
    let messages: { body: any[]; totalRecords: string };
    if (this.pro?.chat_room_key) {
      messages = await this.communication.getProxySharedInbox(this.pro.chat_room_key, page, perPage, true);
    } else if (this.pro?.main_team_id) {
      messages = await this.communication.getSharedInbox(this.pro.main_team_id, page, perPage);
    } else {
      return { body: [], totalRecords: '0' };
    }
    return messages;
  }

  async getInitialMessages(_isPageChange = false): Promise<void> {
    const messagesResponse = await this.getMessagesFromServer(this.page, this.perPage);
    this.messages = messagesResponse.body;
    this.totalMessages = parseInt(messagesResponse.totalRecords);
  }

  async getPageChangeMessages(event: InfiniteScrollCustomEvent) {
    this.page++;
    const {scrollEl, prevScrollHeight, prevScrollTop} = await this.getScrollElement();
    const messagesResponse = await this.getMessagesFromServer(this.page, this.perPage);
    const messages = [...messagesResponse.body, ...this.messages];
    this.messages = this.deduplicateMessages(messages);
    this.totalMessages = parseInt(messagesResponse.totalRecords);
    setTimeout(async () => {
      if (scrollEl) {
        const newScrollHeight = scrollEl.scrollHeight;
        scrollEl.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
      }
      event.target.complete();
      if (this.messages.length >= this.totalMessages) {
        event.target.disabled = true;
      }
    }, 50);
  }

  async getNewMessages() {
    const {scrollEl, prevScrollHeight, prevScrollTop} = await this.getScrollElement();
    const messagesResponse = await this.getMessagesFromServer(1, 20);
    const messages = [...messagesResponse.body, ...this.messages];
    this.messages = this.deduplicateMessages(messages);
    this.totalMessages = parseInt(messagesResponse.totalRecords);
    setTimeout(() => {
      if (scrollEl) {
        const newScrollHeight = scrollEl.scrollHeight;
        const isAtBottom = scrollEl ? (prevScrollTop + scrollEl.clientHeight >= prevScrollHeight - 20) : false;
        if (isAtBottom) {
          scrollEl.scrollTop = newScrollHeight;
        } else {
          scrollEl.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        }
      }
    }, 50);
  }

  async getScrollElement() {
    let scrollEl: HTMLElement | null = null;
    if (this.messageContent) {
      // Mobile: app-scroll-wrapper exposes ionContent
      if (this.messageContent.ionContent && this.messageContent.ionContent.getScrollElement) {
        scrollEl = await this.messageContent.ionContent.getScrollElement();
      } else if (this.messageContent.getScrollElement) {
        // Desktop: ion-content
        scrollEl = await this.messageContent.getScrollElement();
      }
    }
    const prevScrollHeight = scrollEl ? scrollEl.scrollHeight : 0;
    const prevScrollTop = scrollEl ? scrollEl.scrollTop : 0;
    return {scrollEl, prevScrollHeight, prevScrollTop};
  }

  deduplicateMessages(messages) {
    // Duplicate messages are possible when:
    // The user sends a new message then scrolls up to load more
    // When getting new messages every 10 seconds
    // Callers pass [fresh, ...cached]. Keeping the cached copy is fine for text, but an
    // inquiry's status changes after a decline or pre-approve, so it keeps the fresh copy.
    const freshInquiries = new Map<string, any>();
    messages.forEach(message => {
      const messageId = this.getMessageKey(message);
      if (message.type === 'inquiry' && !freshInquiries.has(messageId)) {
        freshInquiries.set(messageId, message);
      }
    });
    const seenIds = new Set();
    messages = messages.slice().reverse().filter(message => {
      const messageId = this.getMessageKey(message);
      if (seenIds.has(messageId)) {
        return false;
      }
      seenIds.add(messageId);
      return true;
    }).map(message => freshInquiries.get(this.getMessageKey(message)) ?? message);
    messages.sort((a, b) => this.compareMessages(a, b));
    return messages;
  }

  // The 10s refresh merges the fresh page into the cached thread in a different order each tick, and
  // Array.sort is stable, so messages sharing a sent_at need an explicit tiebreaker or they flip places.
  private compareMessages(a, b): number {
    const timeA = this.getMessageTime(a);
    const timeB = this.getMessageTime(b);
    if (timeA !== timeB) {
      return timeA - timeB;
    }
    return this.getMessageSortId(a) - this.getMessageSortId(b);
  }

  // An inquiry message is built at read time and has no chat-service id.
  private getMessageKey(message): string {
    return message.type === 'inquiry' ? `inquiry_${message.content?.inquiry?.id}` : `message_${message.id}`;
  }

  // The backend lists an inquiry after a message with the same sent_at, and its id is null.
  private getMessageSortId(message): number {
    if (message?.type === 'inquiry') {
      return Number.MAX_SAFE_INTEGER;
    }
    const id = Number(message?.id);
    return Number.isFinite(id) ? id : 0;
  }

  private getMessageTime(message): number {
    const time = new Date(message?.sent_at).getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
  }

  async handleAssignInput(): Promise<void> {
    this.showAssign = true;
    this.showAssignField = false;
    this.messageForm.get('assignedTo').setValue(this.pro?.data?.assigned_to?.id);
    if (this.pro?.data?.number_type === 'homekeeper') {
      this.assignLabel = 'Assign number to pro';
      const proList = await this.storage.retrieve('proList');
      this.assignList = this.getFilteredProListWithoutProxyNumbers(proList);
    } else {
      this.assignLabel = 'Assign number to property';
      const addressFilter = await this.storage.retrieve('addressFilter');
      this.assignList = addressFilter;
    }
  }

  getFilteredProListWithoutProxyNumbers(proList: any[]): any[] {
    return proList.filter((pro) => !pro.chatRoomKey);
  }

  async assignNumber(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    const loading = await this.util.showLoading();
    try {
      await this.communication.assignChat(
        this.pro.chat_room_key,
        this.messageForm.get('assignedTo').value,
        this.pro?.data?.number_type === 'homekeeper' ? 'homekeeper' : 'address'
      );
      loading.dismiss();
      this.showAssignField = false;
      this.util.showSuccess('Chat assigned successfully');
    } catch (err) {
      loading.dismiss();
      const errorMessage = err.error ? err.error.message : err.message;
      this.util.showError(errorMessage);
    }
  }

  toggleShowAssignField() {
    if (this.demoBlock.block()) { return; }
    this.showAssignField = !this.showAssignField;
  }

  getAssignedToViewValue(value) {
    return this.assignList.find((item) => item?.value === value)?.viewValue || '';
  }

  get contactIconSrc(): string {
    return this.contactInfo?.state === 'known'
      ? 'assets/img/person-white.svg'
      : 'assets/img/add-white-thick.svg';
  }

  // Fallback for threads opened outside Messages (e.g. notifications) that don't
  // pass contactInfo: derive it from the resolved pro/proxy data.
  private deriveContactInfoFromPro(): any {
    const pro = this.pro;
    if (!pro) {
      return null;
    }
    if (pro.chat_room_key && pro.data) {
      const numberType = pro.data.number_type;
      const assigned = pro.data.assigned_to;
      const phone = pro.data.phone_number;
      const chatRoomKey = pro.chat_room_key;
      if (numberType === 'guest') {
        if (assigned?.type === 'Guest' && assigned?.id !== null && assigned?.id !== undefined) {
          return { state: 'known', category: 'guest', guestId: assigned.id };
        }
        return { state: 'unknown', category: 'guest', phone, chatRoomKey };
      }
      if (numberType === 'homekeeper') {
        if (assigned?.type === 'Homekeeper' && assigned?.id !== null && assigned?.id !== undefined) {
          return { state: 'known', category: 'pro', proId: assigned.id };
        }
        return { state: 'unknown', category: 'pro', phone, chatRoomKey, name: pro.data?.assigned_homekeeper?.name };
      }
      return null;
    }
    if (pro.id && !pro.chat_room_key) {
      return { state: 'known', category: 'pro', proId: pro.id };
    }
    return null;
  }

  openContact(): void {
    if (this.demoBlock.block()) { return; }
    const info = this.contactInfo;
    if (!info) { return; }
    if (info.state === 'known' && info.category === 'pro' && info.proId !== null && info.proId !== undefined) {
      this.navCtrl.navigateForward(`my-pro/${info.proId}`, { proId: info.proId });
      return;
    }
    if (info.state === 'known' && info.category === 'guest' && info.guestId !== null && info.guestId !== undefined) {
      this.navCtrl.navigateForward('edit-guest', { mode: 'edit', guestId: info.guestId });
      return;
    }
    if (info.state === 'unknown' && info.category === 'guest') {
      this.navCtrl.navigateForward('edit-guest', { mode: 'create', phone: info.phone, assignToChatRoomKey: info.chatRoomKey, assignType: 'guest' });
      return;
    }
    if (info.state === 'unknown' && info.category === 'pro') {
      this.navCtrl.navigateForward('add-pro', { phone: info.phone, name: info.name, noAddressSelected: true, assignToChatRoomKey: info.chatRoomKey, assignType: 'homekeeper' });
    }
  }

  async callPro() {
    try {
      const proDetail = await this.prosProvider.getProDetail(this.pro.id, this.pro.address_id);
      const phone =
        proDetail?.phone ||
        proDetail?.masked_phone_number ||
        proDetail?.homekeeper?.masked_phone_number ||
        proDetail?.homekeeper?.phone_number ||
        proDetail?.homekeeper?.phone;
      window.location.href = `tel:${phone}`;
    } catch(_err) {
      try {
        const phone =
        this.pro?.phone ||
        this.pro?.masked_phone_number ||
        this.pro?.homekeeper?.masked_phone_number ||
        this.pro?.homekeeper?.phone_number ||
        this.pro?.homekeeper?.phone
        window.location.href = `tel:${phone}`;
      } catch(fallbackErr) {
        this.util.showError(fallbackErr?.error?.message || fallbackErr?.message || 'Error calling pro');
      }
    }
  }

  // Opens the same inquiry detail the Inquiries list and the Schedule open
  // (ReservationPage in inquiry mode). `dialog-params` is written on both
  // branches because the page reads `isInquiry` from storage only.
  async openInquiry(message: any): Promise<void> {
    const inquiry = message?.content?.inquiry;
    if (!inquiry?.id) {
      return;
    }
    const params = {
      guestReservation: { id: inquiry.id },
      isInquiry: true,
      addressId: inquiry.address_id,
      returnTo: 'messages',
    };
    await this.storage.save('dialog-params', params);
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('Inquiry');
      // Resolved from the route registry: importing ReservationPage here closes an import cycle through SharedModule and breaks app boot.
      this.rightSidePanelService.openRightSidePanel(this.rightSidePanelService.getComponentFromRoute('reservation'));
    } else {
      this.navCtrl.navigateForward('reservation', params);
    }
  }

  shouldShowTimeIndicator(message: any, index: number): boolean {
    if (index === 0) {return true;}
    const prevMessage = this.messages[index - 1];
    const currentTime = new Date(message.sent_at);
    const prevTime = new Date(prevMessage.sent_at);
    const diffHours = (currentTime.getTime() - prevTime.getTime()) / (1000 * 60 * 60);
    return diffHours >= 1;
  }

  isLastInGroup(message: any, index: number): boolean {
    if (index === this.messages.length - 1) {return true;}
    const nextMessage = this.messages[index + 1];
    return message.from_member?.type !== nextMessage.from_member?.type;
  }

  goToConciergeMessageAgent(): void {
    const isGuest = this.pro?.data?.number_type !== 'homekeeper';
    const triggerKey = isGuest
      ? 'custom.message_agent.guest_message_received'
      : 'custom.message_agent.pro_message_received';
    if (this.windowService.isDesktopRes) {
      localStorage.setItem('unified_agent_trigger_key', triggerKey);
      this.navCtrl.navigateForward('agent');
    } else {
      this.navCtrl.navigateForward(`agent/triggers/${encodeURIComponent(triggerKey)}`);
    }
  }

  onMessageThumbClick(message: any, type: 'positive' | 'negative'): void {
    if (this.demoBlock.block()) { return; }
    this.activeFeedbackMessageId = message.id;
    this.activeFeedbackType = type;
    setTimeout(async () => {
      const feedbackForm = this.el.nativeElement.querySelector('.concierge-feedback-form');
      if (!feedbackForm) {return;}
      const { scrollEl } = await this.getScrollElement();
      if (scrollEl) {
        const formRect = feedbackForm.getBoundingClientRect();
        const scrollRect = scrollEl.getBoundingClientRect();
        const overflowBottom = formRect.bottom - scrollRect.bottom;
        if (overflowBottom > 0) {
          scrollEl.scrollTop += overflowBottom + 16;
        }
      }
    }, 250);
  }

  cancelMessageFeedback(): void {
    this.dismissFeedback();
  }

  async submitMessageFeedback(message: any): Promise<void> {
    if (this.demoBlock.block()) { return; }
    const textarea = this.el.nativeElement.querySelector('.concierge-feedback-textarea') as HTMLTextAreaElement;
    const feedbackText = textarea?.value?.trim();
    this.isSubmittingFeedback = true;
    const data: any = {
      action_taken_key: 'chat_room_message_sent',
      action_object_id: message.id,
      feedback_status: this.activeFeedbackType === 'positive' ? 'approved' : 'rejected',
    };
    if (feedbackText) {
      data.feedback_text = feedbackText;
    }
    try {
      await lastValueFrom(this.conciergeApi.submitFeedback(data));
      this.dismissFeedback();
      this.util.showSuccess('Feedback submitted');
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to submit feedback');
    } finally {
      this.isSubmittingFeedback = false;
    }
  }

  private dismissFeedback(): void {
    this.closingFeedbackMessageId = this.activeFeedbackMessageId;
    setTimeout(() => {
      this.activeFeedbackMessageId = null;
      this.closingFeedbackMessageId = null;
    }, 200);
  }

}

export const messageThreadDropDownMenu = [
  {
    label: 'View Profile',
    action: 'view-profile'
  },
];
